// src/contexts/StaffContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/api';
import { useAuth } from './CognitoAuthContext';

const StaffContext = createContext();

/**
 * Provider that fetches staff data and provides email-to-name mapping functionality.
 * This allows displaying staff names instead of email addresses throughout the UI
 * without changing the underlying data values.
 */
export const StaffProvider = ({ children }) => {
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Only fetch staff when user is authenticated
    const { isAuthenticated, isLoading: authLoading } = useAuth();

    // Fetch staff list when authenticated
    useEffect(() => {
        // Don't fetch if auth is still loading or user is not authenticated
        if (authLoading || !isAuthenticated) {
            setLoading(false);
            return;
        }
        
        const fetchStaff = async () => {
            try {
                setLoading(true);
                // Use getAllStaff which now returns emails array
                const result = await apiService.getAllStaff();
                console.log('[StaffContext] Fetched staff list:', result);
                console.log('[StaffContext] Staff with emails:', result?.map(s => ({ name: s.staffName, emails: s.emails })));
                setStaffList(result || []);
                setError(null);
            } catch (err) {
                console.error('Error fetching staff list for name mapping:', err);
                setError(err.message);
                setStaffList([]);
            } finally {
                setLoading(false);
            }
        };

        fetchStaff();
    }, [isAuthenticated, authLoading]);

    // Build a lookup map from email -> staff name
    const emailToNameMap = useMemo(() => {
        const map = new Map();
        staffList.forEach(staff => {
            const name = staff.staffName || staff.name;
            const emails = staff.emails || [];
            
            if (name && Array.isArray(emails)) {
                emails.forEach(email => {
                    if (email) {
                        map.set(email.toLowerCase(), name);
                    }
                });
            }
            
            // Also map staffId to name
            const staffId = staff.staffId || staff.id;
            if (staffId && name) {
                map.set(staffId, name);
            }
        });
        return map;
    }, [staffList]);

    /**
     * Converts an email address or staff ID to a display name.
     * If no matching staff is found, returns the original value.
     * 
     * @param {string} emailOrId - Email address or staff ID to convert
     * @returns {string} Staff name if found, otherwise the original value
     */
    const getDisplayName = useCallback((emailOrId) => {
        if (!emailOrId || typeof emailOrId !== 'string') {
            return emailOrId || '';
        }

        // Check exact match (case-insensitive for emails)
        const lowerCaseValue = emailOrId.toLowerCase();
        if (emailToNameMap.has(lowerCaseValue)) {
            return emailToNameMap.get(lowerCaseValue);
        }

        // Check if it's already a staff name (case-sensitive)
        if (emailToNameMap.has(emailOrId)) {
            return emailToNameMap.get(emailOrId);
        }

        // Return original value if no match found
        return emailOrId;
    }, [emailToNameMap]);

    /**
     * Checks if a value looks like an email address
     * @param {string} value 
     * @returns {boolean}
     */
    const isEmail = useCallback((value) => {
        if (!value || typeof value !== 'string') return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }, []);

    /**
     * Gets display name for a DRI/assignee value.
     * Converts emails to names, passes through names as-is.
     * 
     * @param {string} driValue - The DRI/assignee value (email or name)
     * @returns {string} Display name
     */
    const getDriDisplayName = useCallback((driValue) => {
        if (!driValue) return '';
        
        // If it looks like an email, try to convert it
        if (isEmail(driValue)) {
            const displayName = getDisplayName(driValue);
            // Debug: log when email lookup fails
            if (displayName === driValue) {
                console.log('[StaffContext] No name found for email:', driValue, '| Available mappings:', [...emailToNameMap.keys()].slice(0, 10));
            }
            return displayName;
        }
        
        // If not an email, return as-is (already a name)
        return driValue;
    }, [getDisplayName, isEmail, emailToNameMap]);

    const value = {
        staffList,
        loading,
        error,
        getDisplayName,
        getDriDisplayName,
        emailToNameMap
    };

    return (
        <StaffContext.Provider value={value}>
            {children}
        </StaffContext.Provider>
    );
};

/**
 * Hook to access staff context for email-to-name mapping.
 * @returns {{ 
 *   staffList: Array, 
 *   loading: boolean, 
 *   error: string|null, 
 *   getDisplayName: (emailOrId: string) => string,
 *   getDriDisplayName: (driValue: string) => string,
 *   emailToNameMap: Map 
 * }}
 */
export const useStaffContext = () => {
    const context = useContext(StaffContext);
    if (!context) {
        // Return a fallback when used outside provider (for backwards compatibility)
        return {
            staffList: [],
            loading: false,
            error: null,
            getDisplayName: (val) => val || '',
            getDriDisplayName: (val) => val || '',
            emailToNameMap: new Map()
        };
    }
    return context;
};

export default StaffContext;

